import cx from 'classnames'
import { Item } from 'components/CollabItem/Item'
import { MainContent } from 'components/DocRightRail/screens/LayerChanges/components/ChangeCard/MainContent'
import { useUserCard } from 'hooks/queries/users/useUserCard'
import { observer } from 'mobx-react-lite'
import { TrackedChangeType } from 'prose-core'
import Styles from './TrackedChangeItem.module.scss'
import ItemStyles from 'components/CollabItem/CollabItem.module.scss'
import CommentStyles from 'components/CommentV2/CommentItem/CommentItem.module.scss'
import { IconButton } from 'components/IconButton/IconButton'
import { ReactComponent as AcceptIcon } from 'assets/icons/acceptTrackedChange.svg'
import { ReactComponent as RejectIcon } from 'assets/icons/rejectTrackedChange.svg'
import { useDocStore } from 'stores/DocStore'
import { TrackedChangeItemProps } from './Types'
import { ReactControl } from '../ReactControl/ReactControl'
import { useTrackedChange } from 'hooks/queries/trackedChanges/useTrackedChange'
import { useToggleTrackedChangeReaction } from 'hooks/queries/trackedChanges/useToggleTrackedChangeReaction'
import { useCurrentLayer } from 'hooks/useCurrentLayer'
import { DeleteTrackedChangeControl } from '../../CleanupMode/DeleteTrackedChangeControl'
import { MergeTrackedChangeControl } from '../../CleanupMode/MergeTrackedChangeControl'
import { TrackedChangeMetadata } from 'components/TrackedChange/TrackedChangeItem/TrackedChangeMetadata'

const TrackedChangeItem = observer(
  ({ trackedChange, inCleanupMode = false, hasReplies = false }: TrackedChangeItemProps) => {
    const docStore = useDocStore()
    const userQuery = useUserCard(trackedChange.userId, {
      // 1 day
      staleTime: 24 * 60 * 60 * 1000,
      refetchOnMount: false,
    })

    const user = userQuery.data?.user
    const trackedChangeRecord = useTrackedChange({
      articleKey: docStore.articleId,
      layerId: docStore.layerId || '',
      id: trackedChange.uuid,
    })
    const { mutate: toggleReaction } = useToggleTrackedChangeReaction({
      articleKey: docStore.articleId,
      layerId: docStore.layerId || '',
      trackedChangeId: trackedChange.uuid,
    })
    const currentLayer = useCurrentLayer({ articleKey: docStore.articleId })
    const inLayerCleanupMode = inCleanupMode && currentLayer && !currentLayer.is_main

    return (
      <Item.Root
        className={cx(CommentStyles.itemRoot, {
          [CommentStyles.hasReplies]: hasReplies,
        })}
      >
        <Item.Header>
          <Item.Avatar url={user?.avatar_url} name={user?.display_name || undefined} />
          <div className={Styles.headerData}>
            <Item.Actor actorId={trackedChange.userId} name={user?.display_name || null} capitalize />{' '}
            <TrackedChangeMetadata trackedChange={trackedChange} user={user} />
          </div>
          <Item.ControlGroup>
            <ReactControl trackedChange={trackedChange} />
            {!inLayerCleanupMode && (
              <Item.Control
                tooltipTitle={
                  docStore.trackedChangesStore.canRejectChanges ? 'Reject' : "You don't have access to reject"
                }
              >
                <IconButton
                  isDisabled={
                    docStore.trackedChangesStore.isDispositioning || !docStore.trackedChangesStore.canRejectChanges
                  }
                  className={ItemStyles.controlItem}
                  icon={RejectIcon}
                  aria-label="Reject"
                  onPress={() => docStore.trackedChangesStore.reject(trackedChange.uuid)}
                  data-heap-id="tracked-changes-reject-one"
                />
              </Item.Control>
            )}
            {!inLayerCleanupMode && (
              <Item.Control
                tooltipTitle={
                  docStore.trackedChangesStore.canAcceptChanges ? 'Accept' : "You don't have access to accept"
                }
              >
                <IconButton
                  isDisabled={
                    docStore.trackedChangesStore.isDispositioning || !docStore.trackedChangesStore.canAcceptChanges
                  }
                  className={ItemStyles.controlItem}
                  icon={AcceptIcon}
                  aria-label="Accept"
                  onPress={() => docStore.trackedChangesStore.accept([trackedChange.uuid])}
                  data-heap-id="tracked-changes-accept-one"
                />
              </Item.Control>
            )}
            {inLayerCleanupMode && <DeleteTrackedChangeControl trackedChange={trackedChange} />}
            {inLayerCleanupMode && <MergeTrackedChangeControl trackedChange={trackedChange} />}
          </Item.ControlGroup>
        </Item.Header>
        <Item.Body>
          <div
            className={cx({
              [Styles.insertion]: trackedChange.type === TrackedChangeType.Insertion,
              [Styles.deletion]: trackedChange.type === TrackedChangeType.Deletion,
              [Styles.formatting]:
                trackedChange.type === TrackedChangeType.FormattingChange ||
                trackedChange.type === TrackedChangeType.NodeTypeChange,
            })}
          >
            <MainContent nodes={trackedChange.nodes} />
          </div>
        </Item.Body>
        <Item.Reactions reactions={trackedChangeRecord?.reactions || []} onToggleReaction={toggleReaction} />
      </Item.Root>
    )
  },
)

export { TrackedChangeItem }
